import {BaseStore, globalStore, type StoreClass, StoreObject} from "../../../stem-core/state/Store";
import {Money} from "../../../stem-core/localization/Money.js";
import {apiClient, type MerchantConversionRequest, type MerchantScopedRequest} from "../../connection/BlinkApiClient.js";
import type {ExternalAppType} from "../ExternalAppConfigStore";
import type {SocialAccountProvider} from "../SocialAccountStore";
import {field, FieldDescriptor} from "../../../stem-core/state/StoreField.js";
import type {StemDate} from "../../../stem-core/time/Date";
import {BLINK_PAY_URL} from "../../../blinkpay/Constants.js";
import {Payment} from "../PaymentStore";
import {UserProfile} from "../UserProfileStore";
import {Merchant} from "../MerchantStore";
import type {Donation} from "../DonationStore";
import type {MerchantConversion} from "./MerchantConversionStore";
import {type StoreId} from "../../../stem-core/state/State";

interface LogoutUserRequest {
    userId: number;
    sessionId: number;
}

// What MerchantEditUserRequest validates (dashboard/deserializers.py:129)
interface EditUserRequest {
    userId: number;
    emailAddress?: string;
    firstName?: string;
    lastName?: string;
}

interface DisableUserRequest {
    userId: number;
    reactivate: boolean;
}

interface MergeUsersRequest {
    userId: number;
    otherUserId: number;
    finalEmail?: string;
}


@globalStore
export class MerchantUser extends BaseStore("MerchantUser", {dependencies: ["MerchantUserSession"]}) {
    // Copied off the User by MerchantMerchantUserProxy.serializer_include
    declare isActive: boolean;
    // Not from the API: UserDetails assigns the token it just generated onto the instance
    declare merchantUserSession?: any;
    static umMap: Map<string, MerchantUser> = new Map(); // Map by userId and merchantId

    @field(Number) merchantId: number;
    @field("Merchant") merchant: Merchant;
    @field(Number) userId: number;
    @field("UserProfile") user: UserProfile;

    @field("MerchantConversion") conversion: MerchantConversion;

    @field(Date) createdAt: StemDate;
    @field(Boolean) autoPayEnabled: boolean;
    @field(Boolean) useCustomLimit: boolean;
    @field(Number) articlePriceLimit: number;  // TODO: This should be Money, but there is no currency field here!!!

    @field(Object) formResponses: any;
    @field(Boolean) canReceivePromotionalEmails: boolean;

    // Fields only available for merchants
    @field(String) email: string;
    @field(String) name: string;

    @field(String) emailNewsletterEditKey: string;
    @field(Date) consentDate: StemDate;
    @field(Date) lastAccessDate: StemDate;
    @field("MerchantUserSession") lastAccessSession: MerchantUserSession;

    @field(Number) lifetimeRevenue: number;
    @field("Payment") latestDonationPayment: Payment;
    @field(Number) lifetimeDonatedAmount: number;
    @field("Donation") latestDonation: Donation;
    @field("Donation") latestOneTimeDonation: Donation;
    @field("Donation") latestRecurringDonation: Donation;

    @field(Object) metadata: any;

    static makeFieldLoader<T extends StoreObject>(this: StoreClass<T>, fieldDescriptor: FieldDescriptor): (value: any, obj: any) => T {
        fieldDescriptor.cacheField = false;
        fieldDescriptor.rawField = fieldDescriptor.rawField || ((key: string) => key + "Id");

        return (value: any, obj: any) => {
            //const store = obj.getStore(this.objectType);
            return (this as any as typeof MerchantUser).getByUserAndMerchant(value as number, obj.merchantId) as any;
        }
    }

    getEmail(): string {
        return this.email;
    }

    getName(): string {
        return this.name;
    }

    getLastLocation(): MerchantUserSession["location"] | undefined {
        return this.lastAccessSession?.location;
    }

    getLastDevice(): MerchantUserSession["device"] | undefined {
        return this.lastAccessSession?.device;
    }

    getLifetimeRevenue(): Money {
        return new Money(this.lifetimeRevenue, this.merchant.currency.id);
    }

    getLifetimeDonated(): Money {
        return new Money(this.lifetimeDonatedAmount, this.merchant.currency.id);
    }

    getEmailPreferencesLink(): string {
        return `${BLINK_PAY_URL}/email-preferences?edit-key=${this.emailNewsletterEditKey}`;
    }

    static getUMKey(userId: StoreId, merchantId: StoreId): string {
        return `${userId}__${merchantId}`;
    }

    static getByUserAndMerchant(userId: StoreId, merchantId: StoreId): MerchantUser | undefined {
        const key = this.getUMKey(userId, merchantId);
        return this.umMap.get(key);
    }

    static addObject<T extends StoreObject>(this: typeof StoreObject, id: NonNullable<StoreId>, obj: T): void {
        super.addObject<T>(id, obj as any as T);
        const merchantObj = obj as any as MerchantUser;
        const key = (this as typeof MerchantUser).getUMKey(merchantObj.userId, merchantObj.merchantId);
        (this as typeof MerchantUser).umMap.set(key, merchantObj);
    }

    static getFromObject(obj: any): MerchantUser | undefined {
        return this.getByUserAndMerchant(obj.userId, obj.merchantId);
    }
}


@globalStore
export class MerchantUserSession extends BaseStore("MerchantUserSession") {
    @field(Number) merchantId: number;
    @field(Number) userId: number;
    @field(String) key: string; // Optionally, in case we can login as that user
    @field(Date) createdAt: StemDate;
    @field(Date) expiresAt: StemDate;
    @field(Date) revokedAt: StemDate;
    @field(Object) location: {ip?: string; city?: string; region?: string; countryCode?: string};
    @field(Object) device: {type?: string; os?: string; browser?: string};
}


// Without a session id the backend logs the user out of every session
export async function apiMerchantLogoutUser(userSession: {userId: number; id?: StoreId}): Promise<any> {
    return apiClient.post("/merchant/logout_user", {
        userId: userSession.userId,
        sessionId: userSession.id,
    } as LogoutUserRequest)
}


// What UpdateUserMerchantPreferencesRequest validates (users/deserializers.py). The merchant user is taken
// from the session, so the request names only the merchant
export interface UpdateUserMerchantPreferencesRequest {
    merchantId: StoreId;
    autoPayEnabled?: boolean;
    useCustomLimit?: boolean;
    articlePriceLimit?: number;
    formResponses?: Record<string, unknown>;
}

// Overload only, so the emitted signature keeps its default: every caller passes the request
export function apiUpdateUserMerchantPreferences(preferences: UpdateUserMerchantPreferencesRequest): Promise<MerchantUser>;
export async function apiUpdateUserMerchantPreferences(preferences: Partial<UpdateUserMerchantPreferencesRequest> = {}): Promise<MerchantUser> {
    const response = await apiClient.post("/preferences/update_user_merchant_preferences", preferences);
    return MerchantUser.loadObject(response);
}

// MerchantMakeUserTokenRequest (api/dashboard/views.py): makes a session as another user, for support
export interface MerchantMakeUserTokenRequest extends MerchantConversionRequest {
    userId: StoreId;
}

export async function apiMerchantGenerateUserToken(request: MerchantMakeUserTokenRequest): Promise<MerchantUserSession> {
    const response = await apiClient.post("/merchant/fake_user_session", request);
    return MerchantUserSession.loadObject(response);
}

export async function apiMerchantEditUser(merchantUser: MerchantUser, request: Partial<EditUserRequest>): Promise<MerchantUser> {
    const response = await apiClient.post("/merchant/edit_user", {
        userId: merchantUser.userId,
        ...request,
    });
    return MerchantUser.loadObject(response);
}

// MerchantSyncExternalUser. The backend declares provider as ExternalAppType, which shares only CDS and
// FULCO with SocialAccountProvider - the five social ones would fail validation, and what keeps that from
// happening is which panel the call sits in rather than a check
export interface MerchantSyncExternalUserRequest extends MerchantScopedRequest {
    provider: ExternalAppType | SocialAccountProvider;
    externalUserId: string;
    appId?: StoreId;
    email?: string;
}

export async function apiMerchantSyncExternalUser(request: MerchantSyncExternalUserRequest): Promise<any> {
    return apiClient.post("/merchant/sync_external_user", request);
}

export async function apiMerchantDisableUser(merchantUser: MerchantUser, shouldBeActive: boolean): Promise<any> {
    return apiClient.post("/merchant/disable_user", {
        userId: merchantUser.userId,
        reactivate: shouldBeActive,
    } as DisableUserRequest);
}

export async function apiMerchantMergeUsers(merchantUser: MerchantUser, otherMerchantUser: MerchantUser, finalEmail?: string): Promise<any> {
    const response = await apiClient.post("/merchant/merge_users", {
        userId: merchantUser.userId,
        otherUserId: otherMerchantUser.userId,
        finalEmail,
    } as MergeUsersRequest);
    // Let user tables know they should refetch.
    MerchantUser.dispatch("usersMerged", response);
    return response;
}
